import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./TabWhiteList.module.css";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
} from "./OptionListsComponents";
import i18nPackage from "../../../i18n/i18n";
import { CoreList, CoreListRow, CorePagination } from "../../../core";
import { Typography } from "@mui/material";
import useEditableList from "../hooks/useEditableList";
import { EditableListCell, EditableListRemoveButton, EditableListToolbar } from "./EditableList";
import FailIcon from "../../images/check-fail.svg?react";
import PassIcon from "../../images/filled-checkmark.svg?react";
import TabWhiteListIcon from "../../images/configuration/tab-white-list.svg?react";

const PAGE_SIZE = 4;

type AssessmentResourceURLValidation = {
	noSpaces: boolean;
	hasHttp: boolean;
	hasDomain: boolean;
};

function validateAssessmentResourceUrl(url: string): AssessmentResourceURLValidation {
	const isValidDomain = (url: string) => {
		const urlWithoutProtocol = url.replace(/^[a-zA-Z]+(?:[:/])*/, "");
		try {
			const { hostname } = new URL("https://" + urlWithoutProtocol);
			return /\.[a-zA-Z]{2,}$/.test(hostname);
		} catch (error) {
			return false;
		}
	};
	return {
		noSpaces: !url.includes(" "),
		hasHttp: url.startsWith("http://") || url.startsWith("https://"),
		hasDomain: isValidDomain(url),
	};
}

function ValidationRow({
	pass,
	validationKey,
}: {
	pass: boolean;
	validationKey: keyof AssessmentResourceURLValidation;
}) {
	return (
		<div className={styles.validationRow}>
			{pass ? (
				<PassIcon role="img" aria-label={i18nPackage.t("editQuizPage.tabWhiteListTSX.pass")} />
			) : (
				<FailIcon role="img" aria-label={i18nPackage.t("editQuizPage.tabWhiteListTSX.fail")} />
			)}
			<Typography component={"span"}>{i18nPackage.t(`editQuizPage.tabWhiteListTSX.${validationKey}`)}</Typography>
		</div>
	);
}

type TabWhiteListProps = {
	onUpdate: (urls: string[]) => void;
	tabWhiteList: string[];
};

// TO DO ADD THE SUB WEBSTIE OPTION
type UrlObject = {
	url: string;
};

const hasNonWhitespace = (str: string | null | undefined) => typeof str === "string" && /\S/.test(str);

export default function TabWhiteList({ onUpdate, tabWhiteList }: TabWhiteListProps) {
	const memoizedMakeItem = useCallback((val: string) => ({ url: val }), []);
	const memoizedOnUpdate = useCallback((items: UrlObject[]) => onUpdate(items.map((i) => i.url)), [onUpdate]);

	const initialItems = useMemo(
		() => tabWhiteList.map((url) => ({ url })),
		[tabWhiteList], // Only recreates if the actual string array changes
	);

	const { pagination, rows, searchState, tableActions, tableState } = useEditableList<UrlObject>({
		initialItems: initialItems,
		onUpdate: memoizedOnUpdate,
		pageSize: PAGE_SIZE,
		makeItem: memoizedMakeItem,
	});
	const { baseTableRows, tableRows } = rows;
	const { page, setPage, totalPages } = pagination;
	const { searchQuery, setSearchQuery } = searchState;
	const { selectedCount } = tableState;
	const { handleAdd, handleDeleteOne } = tableActions;

	const [urlValidation, setUrlValidation] = useState<AssessmentResourceURLValidation>({
		hasDomain: false,
		hasHttp: false,
		noSpaces: false,
	});
	const allValidationsPassed = Object.values(urlValidation).every((v) => v);

	const validate = useCallback(
		(url: string) => {
			const trimmed = url.trim();
			const existing = baseTableRows.map((x) => x.url);

			const detailedValidation = validateAssessmentResourceUrl(trimmed);

			//  Check for duplicates
			const isDuplicate = existing.some((e) => e.toLowerCase() === trimmed.toLowerCase());

			// Return true ONLY if all technical checks pass AND it's not a duplicate
			const allTechnicalPassed = Object.values(detailedValidation).every((v) => v);
			return allTechnicalPassed && !isDuplicate && trimmed.length > 0;
		},
		[baseTableRows],
	);

	useEffect(() => {
		const trimmed = searchQuery.trim();
		if (trimmed) {
			setUrlValidation(validateAssessmentResourceUrl(trimmed));
		}
	}, [searchQuery]);

	const handleAddWithPrefill = useCallback(() => {
		handleAdd(searchQuery);
		setSearchQuery("https://"); // Reset to prefix after adding
	}, [handleAdd, searchQuery, setSearchQuery]);

	const handleFocus = useCallback(() => {
		if (searchQuery.trim() === "") {
			setSearchQuery("https://");
		}
	}, [searchQuery, setSearchQuery]);

	const handlePaste = useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>) => {
			const pastedText = e.clipboardData.getData("text");

			// If they are pasting a string that already has a protocol
			if (pastedText.startsWith("http")) {
				// override the state with the pasted text
				setSearchQuery(pastedText);

				// Stop the browser from appending the text
				e.preventDefault();
			}
		},
		[setSearchQuery],
	);

	return (
		<BaseListContainer>
			<BaseListHeaderContainer>
				<BaseListInfoArea
					title={i18nPackage.t("editQuizPage.tabWhiteListTSX.title")}
					description={i18nPackage.t("editQuizPage.tabWhiteListTSX.description")}
					slotProps={{
						...(selectedCount !== 0 && {
							badge: {
								text: i18nPackage.t("editQuizPage.tabWhiteListTSX.badgeText", { count: selectedCount }),
							},
						}),
					}}
					icon={<TabWhiteListIcon />}
				/>
				<EditableListToolbar
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					handleAdd={handleAddWithPrefill}
					canAddOrEdit={validate}
					placeholder={i18nPackage.t("editQuizPage.tabWhiteListTSX.searchPlaceholder")}
					slotProps={{
						button: {
							text: i18nPackage.t("editQuizPage.tabWhiteListTSX.addURL"),
						},
						textfield: {
							onFocus: handleFocus,
							onPaste: handlePaste,
						},
					}}
				/>
				{hasNonWhitespace(searchQuery) &&
					(allValidationsPassed ? (
						<div className={styles.validUrlText}>
							<PassIcon />
							<Typography component={"span"}>{i18nPackage.t("editQuizPage.tabWhiteListTSX.validUrl")}</Typography>
						</div>
					) : (
						<div className={styles.validationContainer}>
							{(Object.keys(urlValidation) as Array<keyof AssessmentResourceURLValidation>).map((validationKey) => (
								<ValidationRow key={validationKey} validationKey={validationKey} pass={urlValidation[validationKey]} />
							))}
						</div>
					))}
			</BaseListHeaderContainer>
			<BaseListFooterContainer>
				{tableRows.length === 0 ? null : (
					<CoreList maxHeight={350}>
						{tableRows.map((row) => (
							<CoreListRow
								key={row.id}
								label={<EditableListCell<UrlObject> row={row} dataKey="url" canAddOrEdit={validate} />}
								action={
									!row.editing && (
										<EditableListRemoveButton
											onClick={() => handleDeleteOne(row.id)}
											ariaLabel={i18nPackage.t("editQuizPage.tabWhiteListTSX.deleteAriaLabel", { name: row.url })}
										/>
									)
								}
							/>
						))}
					</CoreList>
				)}
				{baseTableRows.length > PAGE_SIZE && totalPages > 1 && (
					<CorePagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} />
				)}
			</BaseListFooterContainer>
		</BaseListContainer>
	);
}
